import React, { useMemo, useState } from 'react';
import { FiAlertTriangle, FiMinusCircle, FiPlusCircle } from 'react-icons/fi';

/**
 * TableExpander
 * --------------------------------------------------
 * Tabla reutilizable con filas expandibles
 * --------------------------------------------------
 * Props:
 * - columns: [{ header, dataIndex, classHeader, classRow, renderer }]
 * - data: array
 * - rowKey: string (default: 'IDREQUESTITEM')
 * - expandable: boolean
 * - expandField: string (campo array a expandir)
 * - expandColumns: columns del sub-table
 * - placeholderSearch: string
 */

export const TableExpander = ({
  multiExpand = true,
  columns = [],
  data = [],
  rowKey = 'IDREQUESTITEM',
  expandable = true,
  expandField = 'receivings',
  expandColumns = [],
  placeholderSearch = 'Buscar...',
}) => {
  /* ------------------------------------------------------------------ */
  /* State */
  /* ------------------------------------------------------------------ */
  const [expandedRows, setExpandedRows] = useState({});
  const [search, setSearch] = useState('');

  /* ------------------------------------------------------------------ */
  /* Normalización defensiva */
  /* ------------------------------------------------------------------ */
  const safeData = Array.isArray(data) ? data : [];

  /* ------------------------------------------------------------------ */
  /* Handlers */
  /* ------------------------------------------------------------------ */
  const toggleExpand = (id) => {
    setExpandedRows((prev) => {
      // Accordion mode (single expand)
      if (!multiExpand) {
        return prev[id] ? {} : { [id]: true };
      }

      // Multi-expand mode
      return {
        ...prev,
        [id]: !prev[id],
      };
    });
  };

  /* ------------------------------------------------------------------ */
  /* Search */
  /* ------------------------------------------------------------------ */
  const filteredData = useMemo(() => {
    if (!search) return safeData;

    const term = search.toLowerCase();

    return safeData.filter((row) =>
      row &&
      Object.values(row).some((value) =>
        String(value ?? '').toLowerCase().includes(term)
      )
    );
  }, [safeData, search]);

  /* ------------------------------------------------------------------ */
  /* Render */
  /* ------------------------------------------------------------------ */
  return (
    <div className="table-responsive mb-2">
      {/* Search */}
      <div className="mb-2">
        <input
          type="text"
          className="form-control"
          placeholder={placeholderSearch}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <table className="table table-sm table-striped mb-0">
        <thead>
          <tr>
            {expandable && <th style={{ width: 40 }} />}
            {columns.map((col, i) => (
              <th key={i} className={col.classHeader}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {/* Empty state */}
          {filteredData.length === 0 && (
            <tr>
              <td colSpan={columns.length + (expandable ? 1 : 0)}>
                <FiAlertTriangle size={20} /> No hay datos por mostrar
              </td>
            </tr>
          )}

          {/* Rows */}
          {filteredData.map((row) => {
            const rowId = row?.[rowKey];
            const isExpanded = !!expandedRows[rowId];

            return (
              <React.Fragment key={rowId}>
                <tr>
                  {expandable && (
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={() => toggleExpand(rowId)}
                      >
                        {isExpanded ? (
                          <FiMinusCircle size={20} />
                        ) : (
                          <FiPlusCircle size={20} />
                        )}
                      </button>
                    </td>
                  )}

                  {columns.map((col, i) => (
                    <td key={i} className={col.classRow}>
                      {col.renderer
                        ? col.renderer(row[col.dataIndex], row)
                        : row[col.dataIndex] ?? '-'}
                    </td>
                  ))}
                </tr>

                {/* Expanded row */}
                {expandable && isExpanded && (
                  <tr>
                    <td colSpan={columns.length + 1} className="p-0 border-0">
                      <div className="expandable-wrapper open">
                        <ExpandedTable
                          data={row?.[expandField] || []}
                          columns={expandColumns}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Styles */}
      <style>{`
        .expandable-wrapper {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.35s ease, opacity 0.25s ease;
          opacity: 0;
        }
        .expandable-wrapper.open {
          max-height: 500px;
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Expanded Table */
/* ------------------------------------------------------------------ */
const ExpandedTable = ({ columns = [], data = [] }) => {
  const safeData = Array.isArray(data) ? data : [];

  return (
    <table className="table table-sm table-striped mb-0">
      <thead>
        <tr>
          {columns.map((col, i) => (
            <th key={i} className={col.classHeader}>
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {safeData.length === 0 && (
          <tr>
            <td colSpan={columns.length} className="text-center text-muted">
              No hay info. de recepciones
            </td>
          </tr>
        )}

        {safeData.map((row, idx) => (
          <tr key={idx}>
            {columns.map((col, i) => (
              <td key={i} className={col.classRow}>
                {col.renderer
                  ? col.renderer(row[col.dataIndex], row)
                  : row[col.dataIndex] ?? '-'}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
